import React from "react";
import { Volume2 } from "lucide-react";
import { track } from "@/lib/analytics";

/**
 * Compact audio player promoting the teacher's voice.
 *
 * Two visual variants:
 *  - "dark"  → navy card + paper text (default, matches /aula-gratis & bio link)
 *  - "light" → paper card with navy accents (matches sections on light bg)
 *
 * ``eventContext`` is included in the PostHog `teacher_voice_play` event so we
 * can attribute plays to their source (landing_hero, biolink, aula_gratis…).
 */
const TeacherVoicePlayer = ({
  lang = "pt",
  variant = "dark",
  eventContext = "landing",
  className = "",
}) => {
  const isDark = variant === "dark";
  const t = COPY[lang] || COPY.pt;

  return (
    <div
      data-testid={`teacher-voice-player-${eventContext}`}
      className={`rounded-xl border p-5 sm:p-6 ${
        isDark
          ? "border-[#1e2a5a] bg-[#1e2a5a] text-[#f4f1ea]"
          : "border-[#e5e3db] bg-white text-[#1e2a5a]"
      } ${className}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
            isDark ? "bg-[#d9381e]" : "bg-[#1e2a5a]"
          }`}
        >
          <Volume2
            className={`h-5 w-5 ${isDark ? "text-[#f4f1ea]" : "text-[#f4f1ea]"}`}
            strokeWidth={2}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={`text-xs uppercase tracking-[0.18em] ${
              isDark ? "text-[#e5b3b3]" : "text-[#d9381e]"
            }`}
          >
            🎙️ {t.overline}
          </p>
          <p
            className={`mt-2 font-display text-lg sm:text-xl leading-tight ${
              isDark ? "text-[#f4f1ea]" : "text-[#1e2a5a]"
            }`}
          >
            {t.title}
          </p>
          <p
            className={`mt-2 text-sm leading-relaxed ${
              isDark ? "text-[#f4f1ea]/85" : "text-[#5c5c5c]"
            }`}
          >
            {t.subtitle}
          </p>
          <audio
            controls
            preload="metadata"
            src="/audio/intro-lionel.mp3"
            className="mt-4 w-full"
            data-testid={`teacher-voice-audio-${eventContext}`}
            onPlay={() =>
              track("teacher_voice_play", {
                language: lang,
                context: eventContext,
              })
            }
          >
            {t.fallback}
          </audio>
        </div>
      </div>
    </div>
  );
};

const COPY = {
  pt: {
    overline: "A voz do prof",
    title: "Ouça o Prof Lionel — 20 segundos",
    subtitle:
      "Antes de reservar, escuta a voz que vai te ensinar francês. Sotaque parisiense de verdade, sem filtro.",
    fallback: "Seu navegador não suporta áudio.",
  },
  en: {
    overline: "Prof's voice",
    title: "Hear Prof Lionel — 20 seconds",
    subtitle:
      "Before booking, listen to the voice that'll teach you. Real Parisian accent, no filter.",
    fallback: "Your browser doesn't support audio.",
  },
  fr: {
    overline: "Voix du prof",
    title: "Écoute le Prof Lionel — 20 secondes",
    subtitle:
      "Avant de réserver, écoute la voix qui va t'enseigner. Accent parisien véritable, sans filtre.",
    fallback: "Ton navigateur ne supporte pas l'audio.",
  },
};

export default TeacherVoicePlayer;
